/**
 * Save state feature
 * Manages game state persistence in localStorage
 */

import { STORAGE_KEY } from './constants'
import type { SaveState, MinigameState } from './types'
import { LocalStorageManager } from '../../core/LocalStorageManager'

export type { SaveState, MinigameState } from './types'

export class SaveStateManager {
  private static readonly STORAGE_KEY = STORAGE_KEY

  static save(state: SaveState): void {
    try {
      // Use requestIdleCallback if available for non-blocking saves, otherwise save immediately
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            try {
              LocalStorageManager.setItem(this.STORAGE_KEY, JSON.stringify(state))
            } catch (error) {
              console.warn('Failed to save game state:', error)
            }
          },
          { timeout: 100 },
        )
      } else {
        LocalStorageManager.setItem(this.STORAGE_KEY, JSON.stringify(state))
      }
    } catch (error) {
      console.warn('Failed to save game state:', error)
    }
  }

  /**
   * Save state immediately and synchronously (for critical data like choices)
   * This ensures the data is saved before any page refresh or navigation
   */
  static saveImmediate(state: SaveState): void {
    try {
      LocalStorageManager.setItem(this.STORAGE_KEY, JSON.stringify(state))
    } catch (error) {
      console.warn('Failed to save game state immediately:', error)
    }
  }

  static load(): SaveState | null {
    try {
      const saved = LocalStorageManager.getItem(this.STORAGE_KEY)
      if (saved) {
        const state = JSON.parse(saved) as SaveState
        // Migrate old 'items' field to 'inventory' for backward compatibility
        if ((state as any).items && !state.inventory) {
          state.inventory = (state as any).items
          delete (state as any).items
          // Save the migrated state
          this.saveImmediate(state)
        }
        return state
      }
    } catch (error) {
      console.warn('Failed to load game state:', error)
    }
    return null
  }

  static clear(): void {
    // Use LocalStorageManager to ensure systematic clearing
    // This should only be called when starting a new game
    LocalStorageManager.clearGameData()
  }

  static hasProgress(): boolean {
    return this.load() !== null
  }

  static createInitialState(): SaveState {
    return {
      scrollPosition: 0,
      lastLoadedScene: {
        index: -1, // -1 means no scenes loaded
        id: '',
      },
      choices: {},
      minigames: {},
    }
  }

  /**
   * Save inventory to SaveState (stored under rychle-sipy-a-velikonocni-poklad:progress)
   */
  static saveInventory(items: string[]): void {
    const saved = this.load() ?? this.createInitialState()
    saved.inventory = items
    this.saveImmediate(saved)
  }

  /**
   * Load inventory from SaveState (stored under rychle-sipy-a-velikonocni-poklad:progress)
   */
  static loadInventory(): string[] {
    const saved = this.load()
    return saved?.inventory || []
  }

  /**
   * Clear inventory from SaveState
   */
  static clearInventory(): void {
    const saved = this.load()
    if (saved) {
      delete saved.inventory
      this.saveImmediate(saved)
    }
  }
}
